import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Heart, Minus, Plus, ShoppingBag, Star, Truck, Shield, Leaf, Flame } from "lucide-react";
import { AnnouncementBar } from "@/components/home/AnnouncementBar";
import { Navbar } from "@/components/home/Navbar";
import { Footer } from "@/components/home/Footer";
import { ProductCard } from "@/components/shop/ProductCard";
import { products } from "@/lib/products";
import { useCart, useWishlist } from "@/lib/store";
import { cn } from "@/lib/utils";
import { API_BASE_URL } from "@/lib/api";

export const Route = createFileRoute("/shop/$productId")({
  loader: async ({ params }) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/products/${params.productId}`);
      if (res.ok) {
        const product = await res.json();
        return { product };
      }
    } catch (err) {
      // silent fallback
    }
    const product = products.find((p) => p.id === params.productId);
    if (!product) throw notFound();
    return { product };
  },
  head: ({ loaderData }) => {
    const p = loaderData?.product;
    const title = p ? `${p.name} — Royal Lumina` : "Candle — Royal Lumina";
    const desc = p
      ? `${p.name} · ${p.category} candle with ${p.fragrance.toLowerCase()} notes. Hand-poured for ritual and quiet luxury.`
      : "Handcrafted luxury scented candle.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "product" },
        ...(p ? [{ property: "og:image", content: p.img }] : []),
      ],
    };
  },
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="min-h-screen grid place-items-center p-8 text-center">
        <div>
          <h1 className="font-display text-3xl">Something flickered</h1>
          <p className="mt-2 text-muted-foreground">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-6 btn-outline-dark">Try again</button>
        </div>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="min-h-screen grid place-items-center p-8 text-center">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-gold">404</div>
        <h1 className="mt-3 font-display text-4xl">Candle not found</h1>
        <p className="mt-3 text-muted-foreground">This piece may have been discontinued or moved.</p>
        <Link to="/shop" className="mt-6 inline-block btn-gold">Back to shop</Link>
      </div>
    </div>
  ),
  component: ProductPage,
});

const SIZES = [
  { key: "S", label: "Petite · 120g", delta: -100 },
  { key: "M", label: "Signature · 250g", delta: 0 },
  { key: "L", label: "Grand · 400g", delta: 400 },
] as const;

function ProductPage() {
  const { product } = Route.useLoaderData();
  const { add } = useCart();
  const router = useRouter();
  const { has, toggle } = useWishlist();
  const wished = has(product.id);

  const { data: dbProducts = products } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/products`);
        if (res.ok) return await res.json();
      } catch (err) {
        // silent fallback
      }
      return products;
    }
  });

  const [size, setSize] = useState<(typeof SIZES)[number]["key"]>("M");
  const [colorKey, setColorKey] = useState(product.color);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [selectedFragrance, setSelectedFragrance] = useState<string>(
    product.fragranceOptions?.[0] || `${product.fragrance} Signature Blend`
  );

  const gallery = useMemo(() => [product.img, product.img, product.img, product.img], [product.img]);
  const activeSize = SIZES.find((s) => s.key === size)!;
  const finalPrice = product.price + activeSize.delta;

  const related = useMemo(
    () => dbProducts.filter((p: any) => p.id !== product.id && p.category === product.category).slice(0, 4),
    [dbProducts, product.id, product.category],
  );

  const handleAdd = () => {
    if (!product.inStock) return;
    const success = add(product.id, qty, `${activeSize.label} · ${colorKey} · Scent: ${selectedFragrance}`);
    if (success) {
      router.navigate({ to: "/cart" });
    }
  };

  const colorSwatch: Record<string, string> = {
    gold: "#C8A165", cream: "#E8D0A9", green: "#3F8F5E", rose: "#B85450",
  };

  const fragranceIcons: Record<string, string> = {
    Floral: "🌸",
    Woody: "🪵",
    Fresh: "🌿",
    Sweet: "🍯",
    Fruity: "🍊"
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AnnouncementBar />
      <Navbar />

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-28 lg:pt-32 pb-28 lg:pb-20">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground">
          <Link to="/" className="hover:text-gold">Home</Link>
          <span>·</span>
          <Link to="/shop" className="hover:text-gold">Shop</Link>
          <span>·</span>
          <Link to="/shop" search={{ category: product.category }} className="hover:text-gold">{product.category}</Link>
          <span>·</span>
          <span className="text-primary normal-case tracking-normal">{product.name}</span>
        </nav>

        <Link to="/shop" className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold">
          <ChevronLeft className="h-4 w-4" /> Back to shop
        </Link>

        <div className="mt-8 grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-10 lg:gap-16">
          {/* Gallery */}
          <div className="flex flex-col-reverse lg:flex-row gap-4">
            <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible">
              {gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  className={cn(
                    "h-20 w-20 shrink-0 rounded-[14px] overflow-hidden bg-secondary ring-1 transition-all",
                    imgIdx === i ? "ring-2 ring-gold" : "ring-border hover:ring-gold/50",
                  )}
                  aria-label={`View image ${i + 1}`}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            <div className="relative flex-1 aspect-square rounded-[24px] overflow-hidden bg-secondary shadow-luxe">
              <img src={gallery[imgIdx]} alt={product.name} className="h-full w-full object-cover" />
              {product.tag && (
                <span className="absolute top-5 left-5 bg-cream/95 backdrop-blur text-[0.6rem] tracking-[0.25em] uppercase px-3 py-1.5 rounded-full font-medium">
                  {product.tag}
                </span>
              )}
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="text-[0.7rem] tracking-[0.3em] uppercase text-gold">
              {product.category} · {fragranceIcons[product.fragrance] || "✨"} {product.fragrance}
            </div>
            <h1 className="mt-3 font-display text-4xl lg:text-5xl leading-tight">{product.name}</h1>

            <div className="mt-4 flex items-center gap-3">
              <div className="flex items-center gap-1 text-gold">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-4 w-4 ${i < Math.round(product.rating) ? "fill-current" : "opacity-30"}`} strokeWidth={0} />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">{product.rating.toFixed(1)} · {product.reviews} reviews</span>
            </div>

            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-display text-4xl">₹{finalPrice}</span>
              {product.compareAt && <span className="text-lg text-muted-foreground line-through">₹{product.compareAt + activeSize.delta}</span>}
              {product.compareAt && (
                <span className="text-xs tracking-[0.2em] uppercase text-gold">
                  Save ₹{product.compareAt - product.price}
                </span>
              )}
            </div>

            <p className="mt-6 text-muted-foreground leading-relaxed">
              {product.scentDescription || `Hand-poured in small batches from single-origin soy wax, wrapped in a hush of ${product.fragrance.toLowerCase()} notes. Lit at dusk, this candle turns a room into a ritual — steady flame, slow burn, quiet luxury.`}
            </p>

            {/* Size */}
            <div className="mt-8">
              <div className="flex items-center justify-between">
                <div className="text-[0.7rem] tracking-[0.28em] uppercase text-primary font-medium">Size</div>
                <div className="text-xs text-muted-foreground">{activeSize.label}</div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {SIZES.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSize(s.key)}
                    className={cn(
                      "h-14 rounded-[14px] border text-sm transition-all",
                      size === s.key
                        ? "border-gold bg-gold/10 text-primary shadow-luxe font-medium"
                        : "border-border text-muted-foreground hover:border-gold/60 hover:text-primary",
                    )}
                  >
                    <div className="font-display text-lg leading-none">{s.key}</div>
                    <div className="mt-1 text-[0.6rem] tracking-[0.2em] uppercase">
                      {s.delta === 0 ? "Included" : s.delta > 0 ? `+₹${s.delta}` : `-₹${Math.abs(s.delta)}`}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Color / Vessel */}
            <div className="mt-8">
              <div className="flex items-center justify-between">
                <div className="text-[0.7rem] tracking-[0.28em] uppercase text-primary font-medium">Vessel</div>
                <div className="text-xs text-muted-foreground capitalize">{colorKey}</div>
              </div>
              <div className="mt-3 flex gap-3">
                {(["gold", "cream", "green", "rose"] as const).map((k) => (
                  <button
                    key={k}
                    onClick={() => setColorKey(k)}
                    aria-label={k}
                    className={cn(
                      "h-11 w-11 rounded-full ring-offset-2 ring-offset-background transition-all",
                      colorKey === k ? "ring-2 ring-gold" : "ring-1 ring-border hover:ring-gold/50",
                    )}
                    style={{ background: colorSwatch[k] }}
                  />
                ))}
              </div>
            </div>

            {/* Fragrance Selection Option (Horizontal Pills below Vessel) */}
            <div className="mt-8">
              <div className="flex items-center justify-between">
                <div className="text-[0.7rem] tracking-[0.28em] uppercase text-primary font-medium">Fragrance / Khushboo</div>
                <div className="text-xs text-gold font-medium">{selectedFragrance}</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2.5">
                {(product.fragranceOptions || [
                  `${product.fragrance} Signature Blend`,
                  `${product.fragrance} & Warm Oud`,
                  `${product.fragrance} & Soft Musk`
                ]).map((opt: string) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setSelectedFragrance(opt)}
                    className={cn(
                      "px-4 py-3 rounded-[14px] border text-xs transition-all flex items-center gap-2",
                      selectedFragrance === opt
                        ? "border-gold bg-gold/10 text-primary shadow-luxe ring-1 ring-gold font-medium"
                        : "border-border text-muted-foreground hover:border-gold/60 hover:text-primary bg-card"
                    )}
                  >
                    <span>{opt}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Qty + Actions */}
            <div className="mt-8 flex flex-wrap items-stretch gap-3">
              <div className="inline-flex items-center rounded-full border border-border overflow-hidden">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="h-12 w-12 grid place-items-center text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
                  aria-label="Decrease quantity"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <div className="w-10 text-center font-medium tabular-nums">{qty}</div>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="h-12 w-12 grid place-items-center text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
                  aria-label="Increase quantity"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              <button
                onClick={handleAdd}
                disabled={!product.inStock}
                className="flex-1 min-w-[180px] btn-gold justify-center h-12 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <ShoppingBag className="h-4 w-4" /> {product.inStock ? `Add to Cart · ₹${finalPrice * qty}` : "Sold Out"}
              </button>

              <button
                onClick={() => toggle(product.id)}
                aria-label="Wishlist"
                className={cn(
                  "h-12 w-12 rounded-full border grid place-items-center transition-all",
                  wished ? "bg-gold border-gold text-primary" : "border-border text-muted-foreground hover:border-gold hover:text-gold",
                )}
              >
                <Heart className="h-4 w-4" fill={wished ? "currentColor" : "none"} />
              </button>
            </div>

            {/* Perks */}
            <div className="mt-10 grid grid-cols-2 gap-3 text-xs">
              {[
                { icon: Truck, label: "Free shipping over ₹999" },
                { icon: Shield, label: "1-year quality promise" },
                { icon: Leaf, label: "100% natural soy wax" },
                { icon: Flame, label: "40-hour clean burn" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2.5 p-3 rounded-[12px] bg-card border border-border/60">
                  <Icon className="h-4 w-4 text-gold" strokeWidth={1.6} />
                  <span className="text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>

            {/* Fragrance Profile Card ("Kaisi Khushboo Aayegi") */}
            <div className="mt-8 p-5 rounded-[20px] bg-card border border-border/80 shadow-luxe space-y-4">
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{fragranceIcons[product.fragrance] || "✨"}</span>
                  <div>
                    <h3 className="font-display text-lg text-primary">Scent Notes & Atmosphere</h3>
                    <p className="text-[0.7rem] text-muted-foreground">Kaisi khushboo aayegi iss candle se</p>
                  </div>
                </div>
                <span className="text-[0.65rem] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-gold/10 text-gold border border-gold/20">
                  {product.fragrance} Family
                </span>
              </div>

              {product.fragranceNotes ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div className="p-3 rounded-xl bg-muted/30 border border-border/40">
                    <div className="text-[0.65rem] font-semibold uppercase tracking-wider text-gold mb-1">🍊 Top Notes (Pehli Mehak)</div>
                    <div className="text-foreground font-medium">{product.fragranceNotes.top}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/30 border border-border/40">
                    <div className="text-[0.65rem] font-semibold uppercase tracking-wider text-gold mb-1">🌸 Heart Notes (Main Heart)</div>
                    <div className="text-foreground font-medium">{product.fragranceNotes.heart}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/30 border border-border/40">
                    <div className="text-[0.65rem] font-semibold uppercase tracking-wider text-gold mb-1">🪵 Base Notes (Aura & Lingering)</div>
                    <div className="text-foreground font-medium">{product.fragranceNotes.base}</div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  Crafted with 100% natural essential oils and soy wax for an even, room-filling fragrance burn.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-24">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="text-[0.7rem] tracking-[0.3em] uppercase text-gold">You may also love</div>
                <h2 className="mt-2 font-display text-3xl lg:text-4xl">
                  More from the <span className="italic text-gold">{product.category}</span> collection
                </h2>
              </div>
              <Link to="/shop" search={{ category: product.category }} className="hidden md:inline text-sm text-muted-foreground hover:text-gold">
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {related.map((p: any) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  wishlisted={has(p.id)}
                  onWishlist={() => toggle(p.id)}
                  onQuickView={() => {}}
                />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
